import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Grid, Typography, CircularProgress } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import { ShoppingBasketRounded } from '@mui/icons-material';
import { isEmpty, isArray } from 'lodash';
import axios from 'axios';
import { grey } from '@mui/material/colors';
import { Tags } from '../../../Global/Components';

const PREFIX = 'resourceSearch';

const classes = {
  listIconContainer: `${PREFIX}-listIconContainer`,
  tileTitleContainer: `${PREFIX}-tileTitleContainer`,
  listIcon: `${PREFIX}-listIcon`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.listIconContainer}`]: {
    width: 80,
    height: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing(0.25),
  },
  [`& .${classes.tileTitleContainer}`]: {
    padding: theme.spacing(0.5, 1.5),
    flexGrow: 1,
  },
  [`& .${classes.listIcon}`]: {
    width: 80,
    height: 80,
    borderRadius: theme.spacing(0.25),
  },
}));

const SearchTextField = TextField;

export default function ResourceSearch({ onResourceSelect, sx }) {
  const [pattern, setPattern] = useState('');
  const [foundItems, setFoundItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    if (!pattern) {
      setFoundItems([]);
      setLoadingItems(false);
      return;
    }
    setLoadingItems(true);
    axios
      .get('/resources/public/searchResourses', { params: { pattern } })
      .then(({ data }) => {
        setFoundItems(isArray(data) ? data : []);
        setLoadingItems(false);
      })
      .catch((err) => {
        console.error(err);
        setFoundItems([]);
        setLoadingItems(false);
      });
  }, [pattern]);

  return (
    <StyledGrid container>
      <Autocomplete
        freeSolo
        selectOnFocus
        fullWidth
        inputValue={pattern}
        onInputChange={(e, x) => {
          if (e?.type === 'click' && x) return;
          setPattern(x);
        }}
        filterOptions={(x) => x}
        options={isArray(foundItems) ? foundItems : [foundItems]}
        loading={loadingItems && isEmpty(foundItems)}
        getOptionLabel={(option) => option?.title || ''}
        openOnFocus
        value={null}
        onChange={(e, x) => {
          if (x && onResourceSelect) {
            onResourceSelect(x);
          }
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            sx={{
              maxWidth: '800px',
              height: '100px',
              padding: 0,
            }}
          >
            <Grid
              container
              wrap="nowrap"
              sx={{
                maxWidth: '800px',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <Grid
                item
                className={classes.listIconContainer}
                sx={{
                  backgroundColor: option.icon ? 'transparent' : grey[300],
                  width: '100px',
                  height: '100%',
                }}
              >
                {option.icon ? (
                  <img
                    src={option.icon}
                    className={classes.listIcon}
                    alt="icon"
                    style={{
                      maxWidth: '80px',
                      maxHeight: '80px',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <ShoppingBasketRounded fontSize="large" sx={{ color: 'white' }} />
                )}
              </Grid>
              <Grid
                item
                className={classes.tileTitleContainer}
                sx={{
                  maxWidth: '700px',
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {option.title}
                </Typography>
                {option.briefDescription && (
                  <Typography
                    color="textSecondary"
                    sx={{
                      fontSize: '14px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {option.briefDescription}
                  </Typography>
                )}
                {!isEmpty(option.tags) && isArray(option.tags) && (
                  <Grid
                    item
                    sx={{
                      marginTop: '4px',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    <Tags
                      max={8}
                      tags={option.tags}
                      chipProps={{ size: 'small' }}
                      typographyProps={{ variant: 'body2' }}
                      justifyContent="flex-start"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </li>
        )}
        renderInput={(params) => (
          <SearchTextField
            variant="outlined"
            {...params}
            label="Search for a resource"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingItems ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </StyledGrid>
  );
}
